import { Language as LanguageIcon } from '@mui/icons-material'
import { Box, Fab, FormControl, FormControlLabel, FormLabel, Menu, Radio, RadioGroup } from '@mui/material'
import { styled } from '@mui/material/styles'
import { LocalizationProvider as MuiLocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { NextIntlClientProvider } from 'next-intl'
import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'

import { LoadingMessage } from '~/components/Loaders'
import envConfig from '~/configs/env'
import { useCurrentUser } from '~/data/api/hooks/users'
import { FormattingProvider } from '~/data/formatters'
import useDateTimeLocale from '~/hooks/useDateTimeLocale'
import useTranslationMessages from '~/hooks/useTranslationMessages'
import { readCachedPreferredLanguage } from '~/i18n/i18nCache'
import type { I18nConfig } from '~/i18n/types'
import { useGateValue } from '~/configs/statsig'

export default function LocalizationProvider({ children }: { children: React.ReactNode }) {
  const { synopUser } = useCurrentUser()
  const [i18nConfig, setI18nConfig] = useState<I18nConfig>({ blank: false, randomize: false })

  // Resolve a language synchronously on first render so /locales/{lang}.json
  // can start fetching in parallel with /me. Once synopUser resolves, switch
  // to its preference (or defaultLanguage if it has none) — never fall back
  // to earlyLanguage post-resolution, since that would let a stale cached
  // value shadow the authoritative /me response.
  const earlyLanguage = useMemo(() => readCachedPreferredLanguage() ?? envConfig.defaultLanguage, [])
  const preferredLanguage = synopUser ? (synopUser.preferredLanguage ?? envConfig.defaultLanguage) : earlyLanguage
  const translationMessages = useTranslationMessages(preferredLanguage, i18nConfig)

  // Keep <html lang> in sync with the user's preferred language so that
  // assistive tech, search engines, browser spellcheck/hyphenation, and
  // translation tools all see the correct language for the rendered content.
  // The root server layout sets a static `lang="en"` fallback; this effect
  // upgrades it once we know the actual user language. (ENG-8358)
  useEffect(() => {
    if (typeof document !== 'undefined' && preferredLanguage) {
      document.documentElement.lang = preferredLanguage
    }
  }, [preferredLanguage])

  // Show loading while user/translations are being fetched
  if (!synopUser || !translationMessages) {
    return <LoadingMessage />
  }

  return (
    // The `NextIntlClientProvider` will throw an error if the `locale` is undefined. We have to default to some value in case the user has not set a locale.
    <NextIntlClientProvider locale={synopUser.locale ?? envConfig.defaultLocale} messages={translationMessages}>
      <DateTimeLocalizationProvider>
        <FormattingProvider>{children}</FormattingProvider>
      </DateTimeLocalizationProvider>
      <I18nConfigFabComponent i18nConfig={i18nConfig} onConfigChange={setI18nConfig} />
    </NextIntlClientProvider>
  )
}

// Mapping from our supported "datetime locale" to a locale name supported by dayjs. This omits
// locales that map to the language default (e.g. "en-US" -> "en")
const adapterLocaleMap: Record<string, string> = {
  'de-CH': 'de-ch',
  'en-AU': 'en-au',
  'en-CA': 'en-ca',
  'en-GB': 'en-gb',
  'en-IE': 'en-ie',
  'fr-CA': 'fr-ca',
  'fr-CH': 'fr-ch'
}

function DateTimeLocalizationProvider({ children }: PropsWithChildren) {
  const dtLocale = useDateTimeLocale()
  const adapterLocale = adapterLocaleMap[dtLocale] ?? dtLocale.split('-')[0]!
  return (
    <MuiLocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {children}
    </MuiLocalizationProvider>
  )
}

type I18nConfigFabProps = {
  i18nConfig: I18nConfig
  onConfigChange: (config: I18nConfig) => void
}

// TODO: delete this after i18n translation QA is complete
function I18nConfigFabComponent({ i18nConfig, onConfigChange }: I18nConfigFabProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const showTranslationMode = useGateValue('i18n-translation-qa')

  // Do not render the FAB for non-QA users in non-feature-flagged environments
  if (!showTranslationMode) return null

  const handleI18nConfigChange = (value: string) => {
    onConfigChange({
      blank: value === 'blank',
      randomize: value === 'randomize'
    })
  }

  const getCurrentConfigValue = () => {
    if (i18nConfig.blank) return 'blank'
    if (i18nConfig.randomize) return 'randomize'
    return 'normal'
  }

  return (
    <>
      <I18nConfigFab
        color="primary"
        size="medium"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label="i18n configuration"
      >
        <LanguageIcon />
      </I18nConfigFab>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Translation Mode</FormLabel>
            <RadioGroup
              value={getCurrentConfigValue()}
              onChange={(e) => handleI18nConfigChange(e.target.value)}
              name="i18n-config"
            >
              <FormControlLabel value="normal" control={<Radio />} label="Normal" />
              <FormControlLabel value="blank" control={<Radio />} label="Blank Strings" />
              <FormControlLabel value="randomize" control={<Radio />} label="Random Strings" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Menu>
    </>
  )
}

const I18nConfigFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 1_000_000
}))
